import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["sentinel", "dock", "searchInput", "searchHint"];

  // Initializes search state and watches the sentinel to compact the sticky dock.
  // Stimulus calls this when the projects index enters the document.
  // A prefilled three-character query is treated as an already active search.
  connect() {
    const headerHeight = this.siteHeaderHeight();

    this.hasActiveSearch = this.hasSearchInputTarget && this.searchInputTarget.value.trim().length >= 3;
    this.hasSubmittedSearch = this.hasActiveSearch;
    this.updateSearchHint(this.searchInputTarget.value.trim());

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.updateDockPosition(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: `-${headerHeight}px 0px 0px 0px`,
      },
    );

    if (this.hasSentinelTarget) {
      this.observer.observe(this.sentinelTarget);
    }

    this.updateSearchMode();
  }

  // Stops observation when Turbo removes the projects index.
  // Stimulus calls this before Turbo removes the projects index.
  disconnect() {
    this.observer?.disconnect();
  }

  // Submits eligible project searches and clears results after an active search is shortened.
  // Triggered on input events from the search field in projects/_search.html.erb.
  search(event) {
    const searchInput = event.currentTarget;
    const query = searchInput.value.trim();

    this.updateSearchHint(query);

    if (query.length >= 3) {
      this.hasActiveSearch = true;
      this.hasSubmittedSearch = true;
      this.updateSearchMode();
      searchInput.form.requestSubmit();
    } else if (this.hasSubmittedSearch) {
      this.hasSubmittedSearch = false;
      this.submitEmptySearch(searchInput);
    }
  }

  // Submits a blank query while preserving the shorter text the user is still editing.
  // Called by search after a previously eligible query becomes shorter than three characters.
  submitEmptySearch(searchInput) {
    const query = searchInput.value;

    searchInput.value = "";
    searchInput.form.requestSubmit();
    searchInput.value = query;
  }

  // Shows the minimum-length guidance only while the current query cannot be searched.
  // Called on connection and whenever the search input changes.
  updateSearchHint(query) {
    this.searchHintTarget.hidden = query.length >= 3;
  }

  // Keeps the search controls in focus after the user has started a qualifying search.
  // Called on connection and when a query first reaches the three-character minimum.
  updateSearchMode() {
    this.projectHero().hidden = this.hasActiveSearch;
    this.projectCommandCenter().classList.toggle("projects-command-center-search-active", this.hasActiveSearch);
    this.updateDockPosition(this.hasActiveSearch);
  }

  // Toggles the compact appearance while CSS handles sticky positioning.
  // Called by the intersection observer whenever the sentinel crosses the header.
  updateDockPosition(shouldStick) {
    const isSticky = shouldStick || this.hasActiveSearch;

    this.dockTarget.classList.toggle("project-search-dock-stuck", isSticky);
  }

  // Returns the rendered sticky-header height for the observer's pixel-based API.
  // Called during controller connection; the CSS source remains the single height definition.
  siteHeaderHeight() {
    const siteHeader = document.querySelector("header.site-header");

    return siteHeader?.getBoundingClientRect().height || 0;
  }

  // Returns the projects hero, which is a sibling of the search dock controller element.
  // Called when search mode needs to hide or reveal the hero.
  projectHero() {
    return document.querySelector("header.projects-hero");
  }

  // Returns the command-center surface that owns the page's viewport minimum height.
  // Called when search mode needs to let a short result list determine the page height.
  projectCommandCenter() {
    return this.element.closest(".projects-command-center");
  }
}
